import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Icon from "@mui/material/Icon";
import SocialLinksFields from "../FormComponents/SocialLinksFields";
import {
  fetchSocialLinksByParent,
  syncSocialLinksEntries,
  deleteSocialLinkData,
} from "../../services/socialLinksService";

/**
 * Add Social Link Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onSocialLinkAdded - Callback after social link is added successfully
 */
export const AddSocialLinkDialog = ({
  open,
  onClose,
  sessionId,
  userId,
  onSuccess,
  onError,
  onSocialLinkAdded,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  const handleClear = () => {
    setFormData({
      socialtype1: "",
      sociallink1: "",
    });
    setFieldErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.socialtype1 || !formData.socialtype1.trim()) {
      errors.socialtype1 = "Please select a platform";
    }

    if (!formData.sociallink1 || !formData.sociallink1.trim()) {
      errors.sociallink1 = "Please enter a link";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const socialLinksPayload = [
        {
          type: formData.socialtype1,
          link: formData.sociallink1,
        },
      ];

      await syncSocialLinksEntries(sessionId, userId, socialLinksPayload, []);

      // Fetch updated list
      const updatedSocialLinks = await fetchSocialLinksByParent(sessionId, userId);

      if (onSocialLinkAdded) {
        onSocialLinkAdded(updatedSocialLinks);
      }

      handleClear();
      onClose();

      if (onSuccess) {
        onSuccess("Social link added successfully!");
      }
    } catch (error) {
      console.error("Error adding social link:", error);
      if (onError) {
        onError("Failed to add social link. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      handleClear();
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon sx={{ mr: 1, color: "#9C27B0" }}>add_circle</Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Add New Social Link
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <SocialLinksFields
            formData={{
              socialtype1: formData.socialtype1 || "",
              sociallink1: formData.sociallink1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={handleClear} variant="outlined" color="warning" disabled={isLoading}>
          Clear
        </MDButton>
        <MDButton onClick={handleDialogClose} variant="outlined" color="dark" disabled={isLoading}>
          Cancel
        </MDButton>
        <MDButton
          onClick={handleSave}
          variant="gradient"
          sx={{
            background: "linear-gradient(195deg, #9C27B0, #7B1FA2)", // Purple gradient
            color: "white !important", // White text (forced)
            "& .MuiIcon-root": {
              color: "white !important", // White icon (forced)
            },
          }}
          disabled={isLoading}
        >
          {isLoading ? (
            <>
              <CircularProgress size={20} sx={{ mr: 1 }} />
              Adding...
            </>
          ) : (
            "Add Social Link"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Edit Social Link Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {object} socialLinkEntry - Social link entry to edit
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onSocialLinkUpdated - Callback after social link is updated successfully
 */
export const EditSocialLinkDialog = ({
  open,
  onClose,
  socialLinkEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onSocialLinkUpdated,
}) => {
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});

  // Load social link data when dialog opens
  React.useEffect(() => {
    if (open && socialLinkEntry) {
      setFormData({
        socialtype1: socialLinkEntry.type || "",
        sociallink1: socialLinkEntry.link || "",
      });
      setFieldErrors({});
    }
  }, [open, socialLinkEntry]);

  const handleClear = () => {
    setFormData({
      socialtype1: "",
      sociallink1: "",
    });
    setFieldErrors({});
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.socialtype1 || !formData.socialtype1.trim()) {
      errors.socialtype1 = "Please select a platform";
    }

    if (!formData.sociallink1 || !formData.sociallink1.trim()) {
      errors.sociallink1 = "Please enter a link";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    if (!socialLinkEntry || !socialLinkEntry.id) return;

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const socialLinksPayload = [
        {
          id: socialLinkEntry.id,
          type: formData.socialtype1,
          link: formData.sociallink1,
        },
      ];

      await syncSocialLinksEntries(sessionId, userId, socialLinksPayload, []);

      // Fetch updated list
      const updatedSocialLinks = await fetchSocialLinksByParent(sessionId, userId);

      if (onSocialLinkUpdated) {
        onSocialLinkUpdated(updatedSocialLinks);
      }

      onClose();

      if (onSuccess) {
        onSuccess("Social link updated successfully!");
      }
    } catch (error) {
      console.error("Error updating social link:", error);
      if (onError) {
        onError("Failed to update social link. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error for this field when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center" justifyContent="space-between">
          <MDBox display="flex" alignItems="center">
            <Icon color="info" sx={{ mr: 1 }}>
              edit
            </Icon>
            <MDTypography variant="h6" fontWeight="bold">
              Edit Social Link
            </MDTypography>
          </MDBox>
          <IconButton size="small" onClick={handleDialogClose} disabled={isLoading}>
            <Icon>close</Icon>
          </IconButton>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDBox mt={2}>
          <SocialLinksFields
            formData={{
              socialtype1: formData.socialtype1 || "",
              sociallink1: formData.sociallink1 || "",
            }}
            handleChange={handleChange}
            section={1}
            textInputStyle={{}}
            fieldErrors={fieldErrors}
          />
        </MDBox>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <MDButton onClick={handleClear} variant="outlined" color="warning" disabled={isLoading}>
          Clear
        </MDButton>
        <MDButton onClick={handleDialogClose} variant="outlined" color="dark" disabled={isLoading}>
          Cancel
        </MDButton>
        <MDButton onClick={handleSave} variant="gradient" color="info" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Updating...
            </>
          ) : (
            "Update"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Delete Social Link Confirmation Dialog Component
 * @param {boolean} open - Dialog open state
 * @param {function} onClose - Close dialog handler
 * @param {object} socialLinkEntry - Social link entry to delete
 * @param {string} sessionId - Session ID
 * @param {string} userId - User ID
 * @param {function} onSuccess - Success callback with message
 * @param {function} onError - Error callback with message
 * @param {function} onSocialLinkDeleted - Callback after social link is deleted successfully
 */
export const DeleteSocialLinkDialog = ({
  open,
  onClose,
  socialLinkEntry,
  sessionId,
  userId,
  onSuccess,
  onError,
  onSocialLinkDeleted,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    if (!socialLinkEntry || !socialLinkEntry.id) {
      onClose();
      return;
    }

    setIsLoading(true);
    try {
      await deleteSocialLinkData(sessionId, socialLinkEntry.id);

      // Fetch updated list
      const updatedSocialLinks = await fetchSocialLinksByParent(sessionId, userId);

      if (onSocialLinkDeleted) {
        onSocialLinkDeleted(updatedSocialLinks);
      }

      onClose();

      if (onSuccess) {
        onSuccess("Social link deleted successfully!");
      }
    } catch (error) {
      console.error("Error deleting social link:", error);
      if (onError) {
        onError("Failed to delete social link. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDialogClose = () => {
    if (!isLoading) {
      onClose();
    }
  };

  // Platform config for display
  const platformConfig = {
    WhatsApp: { icon: "chat", color: "#25D366", displayName: "WhatsApp" },
    Telegram: { icon: "send", color: "#0088cc", displayName: "Telegram" },
    Instagram: { icon: "photo_camera", color: "#E4405F", displayName: "Instagram" },
    Facebook: { icon: "facebook", color: "#1877F2", displayName: "Facebook" },
    Twitter: { icon: "flutter_dash", color: "#1DA1F2", displayName: "Twitter" },
    LinkedIn: { icon: "business_center", color: "#0A66C2", displayName: "LinkedIn" },
    YouTube: { icon: "play_circle", color: "#FF0000", displayName: "YouTube" },
    Reddit: { icon: "forum", color: "#FF4500", displayName: "Reddit" },
    Pinterest: { icon: "push_pin", color: "#E60023", displayName: "Pinterest" },
    Snapchat: { icon: "camera_alt", color: "#FFFC00", displayName: "Snapchat" },
    TikTok: { icon: "music_note", color: "#000000", displayName: "TikTok" },
    Other: { icon: "language", color: "#666666", displayName: "Other" },
  };

  const platform = socialLinkEntry
    ? platformConfig[socialLinkEntry.type] || platformConfig.Other
    : null;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <MDBox display="flex" alignItems="center">
          <Icon color="error" sx={{ mr: 1 }}>
            warning
          </Icon>
          <MDTypography variant="h6" fontWeight="bold">
            Confirm Deletion
          </MDTypography>
        </MDBox>
      </DialogTitle>
      <DialogContent>
        <MDTypography variant="body2" color="text">
          Are you sure you want to delete this social link?
        </MDTypography>
        {socialLinkEntry && platform && (
          <MDBox mt={2} p={2} sx={{ backgroundColor: "grey.100", borderRadius: 1 }}>
            <MDBox display="flex" alignItems="center" mb={1}>
              <Icon sx={{ color: platform.color, mr: 1 }}>{platform.icon}</Icon>
              <MDTypography variant="body2" fontWeight="medium">
                {platform.displayName}
              </MDTypography>
            </MDBox>
            <MDTypography variant="body2" sx={{ wordBreak: "break-all" }}>
              Link: {socialLinkEntry.link || "N/A"}
            </MDTypography>
          </MDBox>
        )}
        <MDTypography variant="caption" color="error" mt={2} display="block">
          This action cannot be undone.
        </MDTypography>
      </DialogContent>
      <DialogActions>
        <MDButton onClick={handleDialogClose} variant="outlined" color="dark" disabled={isLoading}>
          Cancel
        </MDButton>
        <MDButton onClick={handleConfirm} variant="gradient" color="error" disabled={isLoading}>
          {isLoading ? (
            <>
              <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
              Deleting...
            </>
          ) : (
            "Delete"
          )}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
